<script>
  import { format } from 'svelte-i18n';

  import { Preview } from '@client/components/preview';

  import { AdsService } from '@client/services/ads';

  import { truncate } from '@common/utils/strings';

  /**
   * @typedef {Object} ActivityNote
   * @property {string} [objtype] - Type of the object (e.g., 'adclick', 'myvideoactivity').
   * @property {string} [url] - URL of the visited resource or video.
   * @property {string} [adid] - Advertisement ID.
   * @property {string} [name] - Name/title of the ad or file.
   * @property {string} [linkname] - Name of the clicked link.
   * @property {string} [page_url] - URL of the visited page.
   * @property {string} [page_title] - Title of the visited page.
   * @property {string} [filename] - Name of the downloaded/viewed document.
   * @property {string} [hash] - Unique hash to reference thumbnails.
   */

  /**
   * @typedef {Object} ActivityEntry
   * @property {ActivityNote} [note] - Metadata about the activity.
   */

  /**
   * A single activity entry.
   * @type {ActivityEntry}
   */
  export let entry;

  /**
   * Whether to show ad previews on hover.
   * @type {boolean}
   */
  export let previews = false;

  /**
   * Controls banner preview visibility on hover.
   * @type {boolean}
   */
  let showBannerPreview = false;

  /**
   * Controls document preview visibility on hover.
   * @type {boolean}
   */
  let showDocumentPreview = false;

  /**
   * Simple in-memory cache to store banner data by ad ID.
   * @type {Map<string, any>}
   */
  const bannerCache = new Map();

  /**
   * Fetches and caches a banner's thumbnail data by ad ID.
   * @param {string} adid - Advertisement ID.
   * @returns {Promise<any|null>} Resolves to the banner object or null on failure.
   */
  const bannerThumbnail = async (adid) => {
    if (bannerCache.has(adid)) {
      return bannerCache.get(adid);
    }

    try {
      const banner = await AdsService.getById(adid);
      bannerCache.set(adid, banner);
      return banner;
    } catch (e) {
      bannerCache.set(adid, null);
      return null;
    }
  };

  /**
   * Returns the file extension from a filename.
   * @param {string} filename
   * @returns {string | undefined}
   */
  const getFileExtension = (filename) => filename?.split('.').pop();
</script>

{#if entry.note}
  &nbsp;

  {#if entry.note.objtype === 'myvideoactivity'}
    <a href={entry.note.url} title={entry.note.url} target="_blank">
      {truncate(entry.note.url, 50)}
    </a>
  {:else if entry.note.objtype === 'linkclick'}
    <a href={entry.note.linkname} title={entry.note.linkname} target="_blank">
      {truncate(entry.note.linkname, 50)}
    </a>
  {:else if entry.note.objtype === 'websitevisit'}
    <a href={entry.note.page_url} title={entry.note.page_url} target="_blank">
      {truncate(entry.note.page_title, 50)}
    </a>
  {:else if entry.note.objtype === 'adclick' && previews}
    {#await bannerThumbnail(entry.note.adid)}
      {$format('label.LOADING')}...
    {:then banner}
      {#if banner}
        <a
          class="image-preview"
          href={entry.note.url}
          title={entry.note.url}
          on:mouseenter={() => (showBannerPreview = true)}
          on:mouseleave={() => (showBannerPreview = false)}
          target="_blank"
        >
          {entry.note.name}
          <Preview
            show={showBannerPreview}
            content="<img src='{banner.previewURL}' alt='{banner.title}' loading='lazy' />"
          />
        </a>
      {:else}
        <a href={entry.note.url} title={entry.note.url} target="_blank">
          {entry.note.name}
        </a>
      {/if}
    {/await}
  {:else if entry.note.filename}
    {@const thumbnailUrl = `https://www.sendergen.com/v2/view/${entry.note.hash}/thumbnail.gif`}

    {#key entry.note.hash}
      <a
        class="image-preview"
        href={thumbnailUrl}
        title={thumbnailUrl}
        on:mouseenter={() => (showDocumentPreview = true)}
        on:mouseleave={() => (showDocumentPreview = false)}
        target="_blank"
      >
        {truncate(entry.note.filename, 50, getFileExtension(entry.note.filename))}
        <Preview
          show={showDocumentPreview}
          minWidth="auto"
          disableZoom
          content="<img src={thumbnailUrl} style='max-width: 460px;' alt='{entry.note.filename}' loading='lazy' />"
        />
      </a>
    {/key}
  {/if}
{/if}

<style lang="scss" src="./item-note.scss"></style>
